import { useSearchParams } from "react-router-dom";
import { usePaginate } from "~/components/pagination/usePaginate";
import {
  ExceptionStatus,
  ExceptionType,
  ListExceptionGroupsOrderField,
  OrderDirection,
  useListSpaceExceptionsQuery,
} from "~/operations";

export function usePendingReview({ scopeMrn }: { scopeMrn: string }) {
  const prefix = "reviewPending";
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewPendingParam = searchParams.get(prefix);

  const paginate = usePaginate({ pageSizes: [1], prefix });

  const isOpen = Boolean(reviewPendingParam);

  const handleModalClose = () => {
    const url = paginate.resetUrl(location.href);
    url.searchParams.delete(prefix);
    setSearchParams(url.searchParams);
  };

  const types = [
    ExceptionType.Security,
    ExceptionType.Advisory,
    ExceptionType.Cve,
    ExceptionType.Fex,
  ];

  const pendingExceptions = useListSpaceExceptionsQuery({
    variables: {
      input: {
        scopeMrn,
        types,
        orderBy: {
          direction: OrderDirection.Desc,
          field: ListExceptionGroupsOrderField.CreatedAt,
        },
        filter: {
          statuses: [ExceptionStatus.New],
        },
        includeChildScopes: true,
      },
      ...paginate.pageVars,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    skip: !isOpen,
  });

  const listExceptionGroups = pendingExceptions.data?.listExceptionGroups;

  const pendingExceptionsGroups =
    listExceptionGroups?.edges?.flatMap((eg) => eg.node ?? []) ?? [];

  const totalCount = listExceptionGroups?.totalCount || 0;
  const isEmpty = !pendingExceptions.loading && totalCount === 0;

  const activeUrl = new URL(location.href);
  const openUrl = new URL(activeUrl);
  const closeUrl = paginate.resetUrl(new URL(activeUrl));
  openUrl.searchParams.set(prefix, "true");
  closeUrl.searchParams.delete(prefix);

  return {
    openUrl,
    closeUrl,
    isOpen,
    handleModalClose,
    pendingExceptions,
    pendingExceptionsGroups,
    isEmpty,
    totalCount,
    paginate,
  };
}
